import { RingProgress } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { IconCalendarCheck } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../EmptyState.js';
import { formatDay } from '../format.js';
import { Panel } from '../Panel.js';
import { StatusBadge } from '../StatusBadge.js';
import { followLink, useShell } from '../shell-context.js';
import { TOKENS } from '../tokens.js';
import type { DashboardAlert, DashboardSummary } from './dashboard-types.js';
import classes from './Overview.module.css';

type Tone = 'success' | 'warning' | 'danger' | 'neutral';

/** Halka dilimleri: uyarı dilimi grafik ambarı `warning-500` (M6f G1-30). */
const COLOR: Record<Tone, string> = {
  success: TOKENS['success-fg'],
  warning: TOKENS['warning-500'],
  danger: TOKENS['danger-fg'],
  neutral: `color-mix(in srgb, ${TOKENS['text-secondary']} 45%, ${TOKENS.surface})`,
};

/** Kart bu genişliğe (px) ya da altına inince halka küçülür; halka ve lejant yan yana kalır. */
const NARROW = 340;
const NARROW_RING = 96;

function Breakdown({
  title,
  total,
  rows,
  label,
  size = 132,
  extra,
}: {
  title: string;
  total: number;
  rows: { key: string; label: string; value: number; tone: Tone }[];
  label: string;
  /** Halkanın çapı (px); dar kartta 96. */
  size?: number;
  extra?: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const { ref, width } = useElementSize<HTMLDivElement>();
  const narrow = width > 0 && width <= NARROW;
  const ring = narrow ? Math.min(size, NARROW_RING) : size;
  const number = (value: number) =>
    new Intl.NumberFormat(i18n.resolvedLanguage ?? 'en').format(value);
  const sum = rows.reduce((acc, row) => acc + row.value, 0);
  return (
    <Panel title={title} extra={extra}>
      <div ref={ref} className={classes.layout} data-narrow={narrow || undefined}>
        <RingProgress
          size={ring}
          thickness={Math.round(ring / 8)}
          roundCaps={false}
          aria-label={label}
          className={classes.ring}
          sections={
            sum === 0
              ? [{ value: 100, color: TOKENS['page-bg'] }]
              : rows
                  .filter((row) => row.value > 0)
                  .map((row) => ({ value: (row.value / sum) * 100, color: COLOR[row.tone] }))
          }
          label={
            <div className={classes.center}>
              <span className={classes.total}>{number(total)}</span>
              <span className={classes.totalLabel}>{t('overview.total')}</span>
            </div>
          }
        />
        <dl className={classes.rows}>
          {rows.map((row) => (
            <div key={row.key} className={classes.row}>
              <dt>
                <span className={classes.dot} data-tone={row.tone} aria-hidden />
                {row.label}
              </dt>
              <dd>{number(row.value)}</dd>
            </div>
          ))}
        </dl>
      </div>
    </Panel>
  );
}

/** "Certificate Overview" kartı (personel-sertifikalar.jpeg): halka ve durum sayıları. */
export function CertificateOverview({
  certificates,
  size,
}: {
  certificates: DashboardSummary['certificates'];
  size?: number;
}) {
  const { t } = useTranslation();
  return (
    <Breakdown
      title={t('overview.certificates')}
      label={t('overview.certificates')}
      {...(size ? { size } : {})}
      total={certificates.valid + certificates.expiring + certificates.expired}
      rows={[
        { key: 'valid', label: t('overview.valid'), value: certificates.valid, tone: 'success' },
        {
          key: 'expiring',
          label: t('overview.expiring'),
          value: certificates.expiring,
          tone: 'warning',
        },
        {
          key: 'expired',
          label: t('overview.expired'),
          value: certificates.expired,
          tone: 'danger',
        },
        {
          key: 'missing',
          label: t('overview.missing'),
          value: certificates.missing,
          tone: 'neutral',
        },
      ]}
    />
  );
}

/**
 * Dünkü dinlenme uyumu: kişi günlerinin durumları (ADR 0016–0018). Başlık "Hours of Rest"; gün
 * sağda küçük etiket (M6f G1-33).
 */
export function RestOverview({
  rest,
  formatDate = formatDay,
}: {
  rest: DashboardSummary['rest'];
  /** Gün biçimi; varsayılan `GG.AA.YYYY` (M6f G1-04). */
  formatDate?: (date: string) => string;
}) {
  const { t } = useTranslation();
  const title = t('overview.rest');
  const day = rest.date ? formatDate(rest.date) : null;
  return (
    <Breakdown
      title={title}
      label={day ? t('overview.restOn', { date: day }) : title}
      extra={day ? <span className={classes.date}>{day}</span> : undefined}
      total={rest.compliant + rest.warning + rest.violation + rest.incomplete}
      rows={[
        {
          key: 'compliant',
          label: t('overview.compliant'),
          value: rest.compliant,
          tone: 'success',
        },
        { key: 'warning', label: t('overview.warning'), value: rest.warning, tone: 'warning' },
        { key: 'violation', label: t('overview.violation'), value: rest.violation, tone: 'danger' },
        {
          key: 'incomplete',
          label: t('overview.incomplete'),
          value: rest.incomplete,
          tone: 'neutral',
        },
      ]}
    />
  );
}

type CertificateAlert = Extract<DashboardAlert, { kind: 'certificate' }>;

/** Dolmuş ve dolacak belgeler, en yakın tarih önce (`YYYY-MM-DD` sözlük sırası tarih sırasıdır). */
export function upcomingExpirations(alerts: readonly DashboardAlert[]): CertificateAlert[] {
  return alerts
    .filter((alert): alert is CertificateAlert => alert.kind === 'certificate')
    .sort(
      (a, b) =>
        a.date.localeCompare(b.date) ||
        a.fullName.localeCompare(b.fullName) ||
        a.certificate.localeCompare(b.certificate),
    );
}

/** Belgelerin ekranı: "View All" bağlantısının hedefi. */
const CERTIFICATES_PATH = '/crew/certificates';

/**
 * "Upcoming Expirations" kartı (gemi-personel.jpeg "Crew Dashboard"): Name / Document / Expiry
 * Date; dolmuş belge kırmızı, dolacak belge amber; "View All" belgeler ekranını açar (M6f G1-29).
 */
export function UpcomingExpirations({
  alerts,
  limit = 5,
  formatDate = formatDay,
}: {
  alerts: readonly DashboardAlert[];
  limit?: number;
  formatDate?: (date: string) => string;
}) {
  const { t } = useTranslation();
  const shell = useShell();
  const rows = upcomingExpirations(alerts).slice(0, limit);
  return (
    <Panel
      title={t('overview.expirations.title')}
      flush
      extra={
        <a
          href={CERTIFICATES_PATH}
          className={classes.viewAll}
          draggable={false}
          onClick={(event) => {
            if (shell) followLink(event, CERTIFICATES_PATH, shell.navigate);
          }}
        >
          {t('overview.expirations.viewAll')}
        </a>
      }
    >
      {rows.length === 0 ? (
        <EmptyState
          className={classes.empty}
          icon={<IconCalendarCheck size={22} stroke={1.8} />}
          title={t('overview.expirations.none')}
        />
      ) : (
        <div className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th scope="col">{t('overview.expirations.name')}</th>
                <th scope="col">{t('overview.expirations.document')}</th>
                <th scope="col">{t('overview.expirations.expiry')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.userId}-${row.certificate}-${row.date}`}>
                  <td className={classes.person} title={row.fullName}>
                    {row.fullName}
                  </td>
                  <td title={row.certificate}>{row.certificate}</td>
                  <td>
                    <span className={classes.expiry}>
                      <span className={classes.expiryDate}>{formatDate(row.date)}</span>
                      <StatusBadge
                        tone={row.status === 'expired' ? 'danger' : 'warning'}
                        variant="dot"
                      >
                        {t(`overview.${row.status}`)}
                      </StatusBadge>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
